import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, X, AlertTriangle, Loader2, Info } from 'lucide-react';

const BUNDESLAENDER = [
  'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen',
  'Hamburg', 'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen',
  'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland', 'Sachsen',
  'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen',
];

export function EntwurfBearbeiten({ entwurf, onSave, onCancel, loading, t }) {
  const [form, setForm] = useState({
    freitext: entwurf?.freitext || '',
    bundesland: entwurf?.bundesland || entwurf?.erkannte_daten?.Standort || entwurf?.erkannte_daten?.Bundesland || '',
    schutzstatus: entwurf?.schutzstatus || '',
    erschliessung: entwurf?.erschliessung || '',
    eigentumsform: entwurf?.eigentumsform || '',
    hanglage: entwurf?.hanglage || false,
    bekannte_probleme: entwurf?.bekannte_probleme || '',
  });
  const [blWarning, setBlWarning] = useState(false);

  useEffect(() => {
    if (form.bundesland && entwurf?.bundesland && form.bundesland !== entwurf.bundesland) {
      setBlWarning(true);
    } else {
      setBlWarning(false);
    }
  }, [form.bundesland, entwurf?.bundesland]);

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const schutzOptionen = [
    { value: '', label: t('fa.schutzKeiner') },
    { value: 'nsg', label: t('fa.schutzNsg') },
    { value: 'ffh', label: t('fa.schutzFfh') },
    { value: 'wsg', label: t('fa.schutzWsg') },
    { value: 'lsg', label: t('fa.schutzLsg') },
    { value: 'unbekannt', label: t('fa.schutzUnbekannt') },
  ];

  const erschliessungOptionen = [
    { value: '', label: '–' },
    { value: 'gut', label: t('fa.erschliessungGut') },
    { value: 'teilweise', label: t('fa.erschliessungTeilweise') },
    { value: 'nicht', label: t('fa.erschliessungNicht') },
    { value: 'unbekannt', label: t('fa.erschliessungUnbekannt') },
  ];

  const eigentumOptionen = [
    { value: '', label: '–' },
    { value: 'privat', label: t('fa.eigentumPrivat') },
    { value: 'koerperschaft', label: t('fa.eigentumKoerperschaft') },
    { value: 'staat', label: t('fa.eigentumStaat') },
    { value: 'unbekannt', label: t('fa.eigentumUnbekannt') },
  ];

  return (
    <Card className="border-warm-200 shadow-soft" data-testid="entwurf-bearbeiten">
      <CardContent className="pt-5 pb-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">{t('fa.entwurfTitel')}</h3>

        {/* Bundesland */}
        <div className="mb-4">
          <label className="text-xs font-medium text-foreground mb-1 flex items-center gap-2">
            {t('fa.entwurfBundesland')}
            <span className="text-[10px] font-semibold text-primary/70 bg-primary/5 px-1.5 py-0.5 rounded">{t('fa.entwurfPflicht')}</span>
          </label>
          <p className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            {t('fa.entwurfPflichtWarum')}
          </p>
          <select
            value={form.bundesland}
            onChange={(e) => update('bundesland', e.target.value)}
            className="w-full h-9 px-3 text-sm rounded-md border border-warm-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
            data-testid="entwurf-bundesland-select"
          >
            <option value="">{t('fa.entwurfBundeslandWaehlen')}</option>
            {BUNDESLAENDER.map(bl => (
              <option key={bl} value={bl}>{bl}</option>
            ))}
          </select>
          {blWarning && (
            <div className="flex items-start gap-1.5 mt-1.5 text-xs text-amber-700" data-testid="bundesland-warnung">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {t('fa.entwurfBundeslandGeaendert')}
            </div>
          )}
        </div>

        {/* Fallbeschreibung */}
        <div className="mb-4">
          <label className="text-xs font-medium text-foreground mb-1 block">{t('fa.entwurfFallbeschreibung')}</label>
          <p className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            {t('fa.entwurfFallWarum')}
          </p>
          <Textarea
            value={form.freitext}
            onChange={(e) => update('freitext', e.target.value)}
            className="min-h-[80px] text-sm resize-none"
            data-testid="entwurf-freitext-input"
          />
        </div>

        {/* Schutzstatus + Erschließung */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              {t('fa.entwurfSchutzstatus')}
              <span className="text-[10px] text-muted-foreground/60 font-normal">({t('fa.entwurfOptional').split(' – ')[0].toLowerCase()})</span>
            </label>
            <select
              value={form.schutzstatus}
              onChange={(e) => update('schutzstatus', e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-warm-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
              data-testid="entwurf-schutzstatus-select"
            >
              {schutzOptionen.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5">
              {t('fa.entwurfErschliessung')}
              <span className="text-[10px] text-muted-foreground/60 font-normal">({t('fa.entwurfOptional').split(' – ')[0].toLowerCase()})</span>
            </label>
            <select
              value={form.erschliessung}
              onChange={(e) => update('erschliessung', e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-warm-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
              data-testid="entwurf-erschliessung-select"
            >
              {erschliessungOptionen.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Eigentumsform */}
        <div className="mb-4">
          <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5">
            {t('fa.entwurfEigentumsform')}
            <span className="text-[10px] text-muted-foreground/60 font-normal">({t('fa.entwurfOptional').split(' – ')[0].toLowerCase()})</span>
          </label>
          <select
            value={form.eigentumsform}
            onChange={(e) => update('eigentumsform', e.target.value)}
            className="w-full h-9 px-3 text-sm rounded-md border border-warm-200 bg-white focus:outline-none focus:ring-1 focus:ring-primary/30"
            data-testid="entwurf-eigentumsform-select"
          >
            {eigentumOptionen.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Optionals */}
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('fa.entwurfOptional')}</p>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={form.hanglage}
              onChange={(e) => update('hanglage', e.target.checked)}
              className="rounded border-warm-300"
              data-testid="entwurf-hanglage-checkbox"
            />
            {t('fa.entwurfHanglage')}
          </label>
        </div>

        {/* Bekannte Probleme */}
        <div className="mb-5">
          <label className="text-xs font-medium text-foreground mb-1 block">{t('fa.entwurfBekannteProbleme')}</label>
          <p className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            {t('fa.entwurfBekannteWarum')}
          </p>
          <Textarea
            value={form.bekannte_probleme}
            onChange={(e) => update('bekannte_probleme', e.target.value)}
            placeholder={t('fa.entwurfBekannteHint')}
            className="min-h-[60px] text-sm resize-none"
            data-testid="entwurf-probleme-input"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onCancel} data-testid="entwurf-abbrechen-btn">
            <X className="h-3.5 w-3.5 mr-1.5" />
            {t('fa.entwurfAbbrechen')}
          </Button>
          <Button onClick={() => onSave(form)} disabled={loading} className="flex-1" data-testid="entwurf-speichern-btn">
            {loading ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {t('fa.entwurfSpeichert')}</>
            ) : (
              <><Save className="h-3.5 w-3.5 mr-1.5" /> {t('fa.entwurfSpeichern')}</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
